import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { BFF_BASE_URL } from '../../auth/authApi';

export interface VectorEntry {
  id: string;
  collectionId: string;
  userId: string;
  embedding: number[];
  metadata: Record<string, unknown>;
  createdAt: string;
}

export interface UserVectorSpace {
  userId: string;
  vectorCount: number;
  dimensionality: number;
  createdAt: string;
}

export interface VectorSpaceState {
  entries: VectorEntry[];
  userSpaces: UserVectorSpace[];
  selectedUserId: string | null;
  loading: boolean;
  error: string | null;
  stats: {
    total_vectors: number;
    collections_count: number;
    memory_refs: number;
    knowledge_chunks: number;
  } | null;
}

export const vectorSpaceInitialState: VectorSpaceState = {
  entries: [],
  userSpaces: [],
  selectedUserId: null,
  loading: false,
  error: null,
  stats: null,
};

export const fetchVectorSpaceStats = createAsyncThunk(
  'vectorSpace/fetchStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BFF_BASE_URL}/admin/vector-space/stats`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data?.error || error.message);
      }
      return rejectWithValue('Failed to fetch vector space stats');
    }
  }
);

const vectorSpaceSlice = createSlice({
  name: 'vectorSpace',
  initialState: vectorSpaceInitialState,
  reducers: {
    setVectorEntries: (state, action: PayloadAction<VectorEntry[]>) => {
      state.entries = action.payload;
      state.loading = false;
      state.error = null;
    },
    setUserSpaces: (state, action: PayloadAction<UserVectorSpace[]>) => {
      state.userSpaces = action.payload;
    },
    addVectorEntry: (state, action: PayloadAction<VectorEntry>) => {
      state.entries.push(action.payload);
    },
    deleteVectorEntry: (state, action: PayloadAction<string>) => {
      state.entries = state.entries.filter(e => e.id !== action.payload);
    },
    selectUser: (state, action: PayloadAction<string | null>) => {
      state.selectedUserId = action.payload || null;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchVectorSpaceStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchVectorSpaceStats.fulfilled, (state, action) => {
        state.stats = action.payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchVectorSpaceStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setVectorEntries,
  setUserSpaces,
  addVectorEntry,
  deleteVectorEntry,
  selectUser,
  setLoading,
  setError,
} = vectorSpaceSlice.actions;

export default vectorSpaceSlice;
